/** A tenant's MCP kill switches per tool domain (client MCP plan §9 P0, ORCH-10) — the tenant's
 *  "MCP" tab.
 *
 *  A domain switched off lists none of its tools for the tenant and refuses them, on every MCP route
 *  and the console agent; an apply or revert of its items is refused. A licensee inherits its
 *  franchise parent's switched-off domains; the platform-wide switch (`DjangoMCPDisabledDomains`)
 *  and the tools switched off for every tenant (`DjangoMCPDisabledTools`) are read-only here. Ops
 *  levers for incidents, not commercial gates.
 *
 *  - `GET /tour_operators/{id}/mcp_switches/` (`console_top_view`).
 *  - `PUT` `{domains: {<domain>: bool}, reason}` (`console_mcp_tenant_switches`): only the domains
 *    named change, so two operators switching different domains never undo each other. One config
 *    save carrying the reason (Configuration → Change history), audited (`mcp.tenant_switch_changed`).
 *
 *  The auto-apply ops lock lives on the AI-editing settings (`aiEdit.ts`, `mcp_auto_apply_locked`). */
import { apiGet, apiPut, ConsoleApiError } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type TourOperatorMcpSwitches = Schemas['TourOperatorMcpSwitches'];
export type TourOperatorMcpSwitchDomain = Schemas['TourOperatorMcpSwitchDomain'];
export type TourOperatorMcpSwitchesPut = Schemas['TourOperatorMcpSwitchesPut'];
export type TourOperatorMcpSwitchesUpdated = Schemas['TourOperatorMcpSwitchesUpdated'];

/** The PUT refuses a longer reason. */
export const MCP_SWITCHES_REASON_MAX = 500;

/** The console audit log's event for a change of the switches. */
export const MCP_SWITCHES_AUDIT_EVENT = 'mcp.tenant_switch_changed';

export function mcpSwitches(topId: number): Promise<TourOperatorMcpSwitches> {
  return apiGet(`/tour_operators/${topId}/mcp_switches/`);
}

export function updateMcpSwitches(
  topId: number,
  body: TourOperatorMcpSwitchesPut,
): Promise<TourOperatorMcpSwitchesUpdated> {
  return apiPut(`/tour_operators/${topId}/mcp_switches/`, body);
}

export const mcpSwitchesKeys = {
  all: ['mcpSwitches'] as const,
  switches: (topId: number) => ['mcpSwitches', topId] as const,
};

/** A readable message from a refused save: `{detail}`, or DRF's field-keyed `{field: ["…"]}`
 *  (`domains`, `reason`). */
export function mcpSwitchesErrorText(e: unknown, fallback = 'Save failed.'): string {
  if (!(e instanceof ConsoleApiError)) return e instanceof Error ? e.message : fallback;
  const body = e.body as Record<string, unknown> | undefined;
  if (typeof body?.detail === 'string' && body.detail) return body.detail;
  for (const field of ['domains', 'reason']) {
    const v = body?.[field];
    if (typeof v === 'string' && v) return v;
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  }
  return e.message || fallback;
}
